/**
 * Gamification + analytics derivations.
 *
 * Pure functions over the existing scoring + standings + games data. Nothing
 * here mutates state; the UI calls this on render and gets back ready-to-show
 * badges, leaderboards, and analytics rollups.
 */

import type { Id } from '../models';
import { getDb } from './store';
import { getPlayerStats, getStandings } from './scoringService';

export interface PlayerBadge {
  playerId: Id;
  code:
    | 'first_goal'
    | 'hat_trick'
    | 'hot_streak'
    | 'top_scorer'
    | 'mvp_candidate';
  label: string;
  description: string;
}

export interface TeamBadge {
  teamId: Id;
  code: 'undefeated' | 'comeback' | 'shutout' | 'top_seed';
  label: string;
  description: string;
}

export interface Leaderboard {
  topGoals: Array<{ playerId: Id; teamId: Id; goals: number; assists: number; points: number }>;
  topAssists: Array<{ playerId: Id; teamId: Id; goals: number; assists: number; points: number }>;
  hotPlayers: Array<{ playerId: Id; teamId: Id; goals: number; assists: number; points: number }>;
}

/**
 * Per-player badge set for a tournament. Caller supplies tournamentId; we
 * derive everything else from the engine.
 */
export function computePlayerBadges(tournamentId: Id): PlayerBadge[] {
  const stats = getPlayerStats(tournamentId);
  const db = getDb();

  // First-goal badge: any player with goals > 0 earns "first_goal".
  // Hat trick: any player with >=3 goals in a single game.
  // Top scorer: highest goals (tie-break by points).
  // MVP candidate: top 3 by points.
  // Hot streak: 2+ consecutive games with at least 1 goal.

  const events = db.scoreEvents.filter((e) => e.kind === 'score-event');
  const games = db.games.filter((g) => g.tournamentId === tournamentId);
  const gameIds = new Set(games.map((g) => g.id));

  const goalsByPlayerByGame = new Map<string, Map<string, number>>();
  for (const ev of events) {
    if (!gameIds.has(ev.gameId)) continue;
    if (ev.type !== 'goal') continue;
    if (!ev.playerId) continue;
    const inner = goalsByPlayerByGame.get(ev.playerId) ?? new Map<string, number>();
    inner.set(ev.gameId, (inner.get(ev.gameId) ?? 0) + 1);
    goalsByPlayerByGame.set(ev.playerId, inner);
  }

  const badges: PlayerBadge[] = [];

  if (stats.length > 0) {
    const top = [...stats].sort((a, b) => b.goals - a.goals || b.points - a.points)[0];
    if (top && top.goals > 0) {
      badges.push({
        playerId: top.playerId,
        code: 'top_scorer',
        label: 'Top scorer',
        description: `Leading with ${top.goals} goal${top.goals === 1 ? '' : 's'}.`,
      });
    }
    const mvpPool = [...stats].sort((a, b) => b.points - a.points).slice(0, 3);
    for (const m of mvpPool) {
      if (m.points === 0) continue;
      if (badges.some((b) => b.playerId === m.playerId && b.code === 'top_scorer')) continue;
      badges.push({
        playerId: m.playerId,
        code: 'mvp_candidate',
        label: 'MVP candidate',
        description: `${m.points} points across ${m.gamesPlayed} game${m.gamesPlayed === 1 ? '' : 's'}.`,
      });
    }
  }

  for (const s of stats) {
    if (s.goals > 0 && !badges.some((b) => b.playerId === s.playerId)) {
      badges.push({
        playerId: s.playerId,
        code: 'first_goal',
        label: 'First goal',
        description: 'On the board.',
      });
    }
    const perGame = goalsByPlayerByGame.get(s.playerId);
    if (perGame && Array.from(perGame.values()).some((g) => g >= 3)) {
      badges.push({
        playerId: s.playerId,
        code: 'hat_trick',
        label: 'Hat trick',
        description: 'Three goals in a single game.',
      });
    }
    if (perGame && perGame.size >= 2) {
      badges.push({
        playerId: s.playerId,
        code: 'hot_streak',
        label: 'Hot streak',
        description: `Goals in ${perGame.size} different games.`,
      });
    }
  }

  return badges;
}

export function computeTeamBadges(tournamentId: Id): TeamBadge[] {
  const standings = getStandings(tournamentId);
  const db = getDb();
  const games = db.games.filter((g) => g.tournamentId === tournamentId && g.status === 'final');
  const results = db.gameResults.filter((r) => games.some((g) => g.id === r.gameId));

  const badges: TeamBadge[] = [];

  for (const s of standings) {
    if (s.gamesPlayed >= 2 && s.losses === 0 && s.wins > 0) {
      badges.push({
        teamId: s.teamId,
        code: 'undefeated',
        label: 'Undefeated',
        description: `${s.wins}-${s.losses}-${s.ties}.`,
      });
    }
  }

  if (standings.length > 0) {
    const sorted = [...standings].sort((a, b) => (b.points ?? 0) - (a.points ?? 0));
    const top = sorted[0];
    if (top && (top.points ?? 0) > 0) {
      badges.push({
        teamId: top.teamId,
        code: 'top_seed',
        label: 'Top seed',
        description: `${top.points} points so far.`,
      });
    }
  }

  // Shutout: any final result with 0 against.
  for (const r of results) {
    const game = games.find((g) => g.id === r.gameId);
    if (!game) continue;
    if (r.homeScore > 0 && r.awayScore === 0) {
      if (!badges.some((b) => b.teamId === game.homeTeamId && b.code === 'shutout')) {
        badges.push({
          teamId: game.homeTeamId,
          code: 'shutout',
          label: 'Shutout',
          description: `Held an opponent scoreless ${r.homeScore}-0.`,
        });
      }
    }
    if (r.awayScore > 0 && r.homeScore === 0) {
      if (!badges.some((b) => b.teamId === game.awayTeamId && b.code === 'shutout')) {
        badges.push({
          teamId: game.awayTeamId,
          code: 'shutout',
          label: 'Shutout',
          description: `Held an opponent scoreless ${r.awayScore}-0.`,
        });
      }
    }
  }

  return badges;
}

export function computeLeaderboard(tournamentId: Id): Leaderboard {
  const stats = getPlayerStats(tournamentId).map((s) => ({
    playerId: s.playerId,
    teamId: s.teamId,
    goals: s.goals,
    assists: s.assists,
    points: s.points,
  }));
  const topGoals = [...stats].sort((a, b) => b.goals - a.goals).slice(0, 10);
  const topAssists = [...stats].sort((a, b) => b.assists - a.assists).slice(0, 10);
  const hotPlayers = [...stats].sort((a, b) => b.points - a.points).slice(0, 5);
  return { topGoals, topAssists, hotPlayers };
}

// --------------------------------------------------------------------------
// Commissioner-level analytics
// --------------------------------------------------------------------------

export interface TournamentAnalytics {
  totalGames: number;
  finalGames: number;
  inProgress: number;
  upcoming: number;
  goalsPerGame: number;
  averageGapMinutesByTeam: Array<{ teamId: Id; teamName: string; averageGapMinutes: number; games: number }>;
  backToBackCount: number;
  topTeamsByPoints: Array<{ teamId: Id; teamName: string; points: number }>;
  topPlayersByPoints: Array<{ playerId: Id; playerName: string; teamName: string; points: number }>;
  recap: string;
}

export function computeTournamentAnalytics(tournamentId: Id): TournamentAnalytics {
  const db = getDb();
  const games = db.games.filter((g) => g.tournamentId === tournamentId);
  const finals = games.filter((g) => g.status === 'final');
  const inProgress = games.filter((g) => g.status === 'in_progress');
  const upcoming = games.filter((g) => g.status === 'scheduled' || g.status === 'ready');
  const results = db.gameResults.filter((r) => games.some((g) => g.id === r.gameId));
  const totalGoals = results.reduce((acc, r) => acc + r.homeScore + r.awayScore, 0);
  const goalsPerGame = finals.length > 0 ? totalGoals / finals.length : 0;

  // Average gap between games per team.
  const slotById = new Map(db.scheduleSlots.map((s) => [s.id, s]));
  const teams = db.teams.filter((t) => t.tournamentId === tournamentId);
  const averageGapMinutesByTeam = teams
    .map((team) => {
      const teamGames = games
        .filter((g) => g.homeTeamId === team.id || g.awayTeamId === team.id)
        .map((g) => g.scheduleSlotId ? slotById.get(g.scheduleSlotId) : undefined)
        .filter((s): s is NonNullable<typeof s> => !!s)
        .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
      if (teamGames.length < 2) return { teamId: team.id, teamName: team.name, averageGapMinutes: 0, games: teamGames.length };
      let total = 0;
      for (let i = 1; i < teamGames.length; i++) {
        total += (new Date(teamGames[i].startsAt).getTime() - new Date(teamGames[i - 1].endsAt).getTime()) / 60000;
      }
      return {
        teamId: team.id,
        teamName: team.name,
        averageGapMinutes: Math.round(total / (teamGames.length - 1)),
        games: teamGames.length,
      };
    })
    .sort((a, b) => a.averageGapMinutes - b.averageGapMinutes);

  // Back-to-back: gap < 30 minutes.
  const backToBackCount = averageGapMinutesByTeam.filter((t) => t.games >= 2 && t.averageGapMinutes < 30).length;

  const standings = getStandings(tournamentId);
  const topTeamsByPoints = standings
    .slice()
    .sort((a, b) => (b.points ?? 0) - (a.points ?? 0))
    .slice(0, 5)
    .map((s) => ({
      teamId: s.teamId,
      teamName: teams.find((t) => t.id === s.teamId)?.name ?? '—',
      points: s.points ?? 0,
    }));

  const playerStats = getPlayerStats(tournamentId).slice(0, 5);
  const topPlayersByPoints = playerStats.map((s) => {
    const player = db.players.find((p) => p.id === s.playerId);
    const profile = player ? db.profiles.find((p) => p.id === player.profileId) : null;
    const team = teams.find((t) => t.id === s.teamId);
    return {
      playerId: s.playerId,
      playerName: profile?.displayName ?? 'Unknown',
      teamName: team?.name ?? '—',
      points: s.points,
    };
  });

  // Deterministic recap copy — friendly tone, no fabricated numbers.
  const recapBits: string[] = [];
  if (finals.length === 0) {
    recapBits.push('No games have wrapped yet — this recap will fill in as results land.');
  } else {
    recapBits.push(`${finals.length} game${finals.length === 1 ? '' : 's'} in the books with ${totalGoals} goal${totalGoals === 1 ? '' : 's'} (~${goalsPerGame.toFixed(1)}/game).`);
    if (topPlayersByPoints[0]) {
      recapBits.push(`Top of the leaderboard: ${topPlayersByPoints[0].playerName} (${topPlayersByPoints[0].teamName}) with ${topPlayersByPoints[0].points} points.`);
    }
    if (topTeamsByPoints[0]) {
      recapBits.push(`Pace-setter: ${topTeamsByPoints[0].teamName} at ${topTeamsByPoints[0].points} points.`);
    }
    if (backToBackCount > 0) {
      recapBits.push(`${backToBackCount} team${backToBackCount === 1 ? '' : 's'} are running tight back-to-backs — worth a glance.`);
    }
  }

  return {
    totalGames: games.length,
    finalGames: finals.length,
    inProgress: inProgress.length,
    upcoming: upcoming.length,
    goalsPerGame,
    averageGapMinutesByTeam,
    backToBackCount,
    topTeamsByPoints,
    topPlayersByPoints,
    recap: recapBits.join(' '),
  };
}
